import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { projectsAPI, projectFoldersAPI } from '../services/api';
import { Project, ProjectFolder } from '../types';
import ProjectTasksList from './ProjectTasksList';
import ProjectTasksBoard from './ProjectTasksBoard';
import ProjectTasksTimeline from './ProjectTasksTimeline';
import ProjectTasksCalendar from './ProjectTasksCalendar';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [folder, setFolder] = useState<ProjectFolder | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [grouping, setGrouping] = useState('none');

  // Učitaj poslednji korišćeni view (tab) za projekat iz localStorage
  useEffect(() => {
    if (!projectId) return;
    try {
      const stored = localStorage.getItem('projectLastView');
      if (stored) {
        const parsed: Record<string, number> = JSON.parse(stored);
        const index = parsed[projectId];
        if (typeof index === 'number' && index >= 0 && index <= 3) {
          setActiveTab(index);
        }
      }
    } catch {
      // ignoriši grešku u čitanju
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const data = await projectsAPI.getById(projectId!);
      setProject(data);
      // Učitaj folder ako postoji
      if (data.userFolderId) {
        try {
          const folderData = await projectFoldersAPI.getById(data.userFolderId);
          setFolder(folderData);
        } catch (err) {
          console.error('Error loading folder:', err);
        }
      }
    } catch (err: any) {
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !project) {
    return <div style={{ padding: '2rem' }}>Učitavanje...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666' }}>
          <Button
            icon="pi pi-arrow-left"
            label="Projekti"
            className="p-button-text p-button-sm"
            onClick={() => navigate('/projects')}
            style={{ padding: 0 }}
          />
          {folder && (
            <>
              <span>/</span>
              <span
                style={{ cursor: 'pointer', color: folder.color }}
                onClick={() => navigate(`/projects/folder/${folder.slug}`)}
              >
                {folder.name}
              </span>
            </>
          )}
        </div>
        <h1 style={{ margin: 0 }}>{project.name}</h1>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Dropdown
          value={grouping}
          options={[{ label: 'Bez grupisanja', value: 'none' }]}
          onChange={(e) => setGrouping(e.value)}
          appendTo="self"
        />
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={(e) => {
          setActiveTab(e.index);
          if (!projectId) return;
          try {
            const stored = localStorage.getItem('projectLastView');
            const parsed: Record<string, number> = stored ? JSON.parse(stored) : {};
            parsed[projectId] = e.index;
            localStorage.setItem('projectLastView', JSON.stringify(parsed));
          } catch {
            // ignoriši grešku u upisu
          }
        }}
      >
        <TabPanel header="List">
          <ProjectTasksList projectId={projectId!} />
        </TabPanel>
        <TabPanel header="Board">
          <ProjectTasksBoard projectId={projectId!} />
        </TabPanel>
        <TabPanel header="Timeline">
          <ProjectTasksTimeline projectId={projectId!} />
        </TabPanel>
        <TabPanel header="Calendar">
          <ProjectTasksCalendar projectId={projectId!} />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default ProjectDetail;

